import React, { useState } from 'react'
import { Box, Paper, Typography, Button, Switch, FormControlLabel, IconButton, Collapse } from '@mui/material'
import { useConnectivity } from '../../contexts/ConnectivityContext'
import { useAuth } from '../../contexts/AuthContext'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import WifiIcon from '@mui/icons-material/Wifi'
import WifiOffIcon from '@mui/icons-material/WifiOff'

function statusLabel(state) {
  if (state === 'OFFLINE') return 'Offline'
  if (state === 'RECOVERING') return 'Reconnecting'
  if (state === 'ONLINE') return 'Online'
  return 'Checking'
}

export function ConnectivityDevTools() {
  const { 
    connectivityState, 
    isOffline, 
    isOnline, 
    isRecovering,
    isElectron,
    forceOffline, 
    forceOnline, 
    setAutoMode,
    checkConnectivity 
  } = useConnectivity()
  const { role } = useAuth()
  
  const [expanded, setExpanded] = useState(false)
  const [checking, setChecking] = useState(false)

  if (!isElectron || role !== 'superadmin') {
    return null
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleCheckNow = async () => {
    setChecking(true)
    try {
      const result = await checkConnectivity()
      console.log('[CONNECTIVITY] Manual check result:', result)
    } catch (error) {
      console.error('[CONNECTIVITY] Manual check failed:', error)
    } finally {
      setChecking(false)
    }
  }

  const handleToggleOffline = async () => {
    if (isOffline) {
      await setAutoMode()
    } else {
      await forceOffline()
    }
  }

  const getStateColor = () => {
    if (isOnline) return '#4caf50'
    if (isRecovering) return '#ff9800'
    if (isOffline) return '#f57c00'
    return '#9e9e9e'
  }

  return (
    <Paper
      data-sb-keep-connectivity="1"
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: expanded ? 320 : 200,
        zIndex: 1400,
        boxShadow: 3,
        transition: 'width 0.3s ease',
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: getStateColor(),
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isOffline ? <WifiOffIcon fontSize="small" /> : <WifiIcon fontSize="small" />}
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {statusLabel(connectivityState.state)}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Mode: {connectivityState.mode}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
            Reason: {connectivityState.reason}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isOffline}
                  onChange={handleToggleOffline}
                  color="warning"
                />
              }
              label={
                <Typography variant="body2">
                  {isOffline ? 'Force Offline' : 'Force Offline'}
                </Typography>
              }
            />

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              fullWidth
            >
              Refresh Page
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleCheckNow}
              disabled={checking}
              fullWidth
            >
              {checking ? 'Checking...' : 'Check Now'}
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={setAutoMode}
              disabled={connectivityState.mode === 'AUTO'}
              fullWidth
            >
              Reset to Auto
            </Button>
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', fontSize: '0.65rem' }}>
            Last change: {new Date(connectivityState.lastChangeAt).toLocaleTimeString()}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  )
}
